import { Tooltip } from 'antd';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  getAccessToDashboard,
  getAccessToAlertList,
  getAccessToAlertHistory,
  getAccessToCasesByDepartment,
  getAccessToCaseResolutionReview,
  getAccessToSettings,
  getAccessToRegulatoryReport,
  getAccessToOperationalReport,
  getAccessToWhiteList,
  getCanSeeFramlView
} from '../../redux/selectors/cm/access';
import {
  CASE_LIST_ROUTES,
  CM_BASE_ROUTE,
  SETTINGS_LIST_ROUTES,
  REPORTS_ROUTES
} from '../constants/cmConstants';
import {
  DeliveredProcedureOutlined,
  DeploymentUnitOutlined
} from '@ant-design/icons';

const ListItem = ({
  styleClasses,
  menuCollapse,
  pathname,
  icon,
  text,
  useAntdIcon = false
}) => (
  <Tooltip placement="right" title={text}>
    <li
      className={classNames(
        menuCollapse ? styleClasses.accordionCollaps : styleClasses.accordion
      )}>
      <Link className={styleClasses.linkItem} to={{ pathname }}>
        {useAntdIcon ? (
          <span style={{ fontSize: '22px' }}>{icon}</span>
        ) : (
          <i className={`fa fa-2x ${icon}`} />
        )}
        <br />
        <span className={styleClasses.parentText}>{text}</span>
      </Link>
    </li>
  </Tooltip>
);

export const SidebarItems = ({
  menuCollapse,
  styleClasses,
  hasAccessToDashboard,
  hasAccessToAlertList,
  hasAccessToAlertHistory,
  hasAccessToCasesByDepartment,
  hasAccessToCaseResolutionReview,
  hasAccessToSettings,
  hasAccessToRegulatoryReport,
  hasAccessToOperationalReport,
  hasAccessToWhiteList,
  canSeeFramlView
}) => {
  return (
    <ul className={styleClasses.accordionList}>
      {hasAccessToDashboard && (
        <ListItem
          styleClasses={styleClasses}
          menuCollapse={menuCollapse}
          pathname={CM_BASE_ROUTE}
          icon="fa-compass"
          text="Dashboard"
        />
      )}
      {hasAccessToAlertList && (
        <ListItem
          styleClasses={styleClasses}
          menuCollapse={menuCollapse}
          pathname={`${CM_BASE_ROUTE}/alerts`}
          icon="fa-list"
          text="Alert List"
        />
      )}
      {hasAccessToCasesByDepartment && (
        <ListItem
          styleClasses={styleClasses}
          menuCollapse={menuCollapse}
          pathname={`${CM_BASE_ROUTE}/${CASE_LIST_ROUTES.BY_DEPARTMENT}`}
          icon="fa-list-alt"
          text="Case List"
        />
      )}
      {/* FIN-11360 FRAML View — CM left-nav entry; mock places it directly above
          Tickets Dashboard. antd icon (not fa-*) so it renders without the paid
          FontAwesome Pro glyph set (FIN-12088 made the FA token optional).
          FIN-14761: gated on AAA resource AND the env kill switch, like the route. */}
      {canSeeFramlView && (
        <ListItem
          styleClasses={styleClasses}
          menuCollapse={menuCollapse}
          pathname={`${CM_BASE_ROUTE}/framl`}
          icon={<DeploymentUnitOutlined />}
          text="FRAML View"
          useAntdIcon={true}
        />
      )}
      {hasAccessToCaseResolutionReview && (
        <ListItem
          styleClasses={styleClasses}
          menuCollapse={menuCollapse}
          pathname={`${CM_BASE_ROUTE}/to-check`}
          icon="fa-ticket"
          text="Tickets Dashboard"
        />
      )}
      {hasAccessToAlertHistory && (
        <ListItem
          styleClasses={styleClasses}
          menuCollapse={menuCollapse}
          pathname={`${CM_BASE_ROUTE}/alerts-retry-history`}
          icon={<DeliveredProcedureOutlined />}
          text="Alert Retry History"
          useAntdIcon={true}
        />
      )}
      {hasAccessToSettings && (
        <ListItem
          styleClasses={styleClasses}
          menuCollapse={menuCollapse}
          pathname={`${CM_BASE_ROUTE}/${SETTINGS_LIST_ROUTES.EMAIL_PREFERENCES}`}
          icon="fa fa-wrench"
          text="Settings"
        />
      )}
      {(hasAccessToRegulatoryReport || hasAccessToOperationalReport) && (
        <ListItem
          styleClasses={styleClasses}
          menuCollapse={menuCollapse}
          pathname={`${CM_BASE_ROUTE}/${
            hasAccessToRegulatoryReport
              ? REPORTS_ROUTES.REGULATORY_REPORT
              : REPORTS_ROUTES.OPERATIONAL_REPORT
          }`}
          icon="fa fa-inbox"
          text="Reports"
        />
      )}
      {hasAccessToWhiteList && (
        <ListItem
          styleClasses={styleClasses}
          menuCollapse={menuCollapse}
          pathname={`${CM_BASE_ROUTE}/whitelist`}
          icon="fa fa-file-text"
          text="Whitelist"
        />
      )}
    </ul>
  );
};

ListItem.propTypes = {
  styleClasses: PropTypes.object.isRequired,
  menuCollapse: PropTypes.bool.isRequired,
  pathname: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  useAntdIcon: PropTypes.bool
};

SidebarItems.propTypes = {
  menuCollapse: PropTypes.bool.isRequired,
  styleClasses: PropTypes.object.isRequired,
  hasAccessToDashboard: PropTypes.bool,
  hasAccessToAlertList: PropTypes.bool,
  hasAccessToAlertHistory: PropTypes.bool,
  hasAccessToCasesByDepartment: PropTypes.bool,
  hasAccessToCaseResolutionReview: PropTypes.bool,
  hasAccessToSettings: PropTypes.bool,
  hasAccessToRegulatoryReport: PropTypes.bool,
  hasAccessToOperationalReport: PropTypes.bool,
  hasAccessToWhiteList: PropTypes.bool,
  canSeeFramlView: PropTypes.bool
};

const mapStateToProps = (state) => {
  const hasAccessToDashboard = getAccessToDashboard(state);
  const hasAccessToAlertList = getAccessToAlertList(state);
  const hasAccessToAlertHistory = getAccessToAlertHistory(state);
  const hasAccessToCasesByDepartment = getAccessToCasesByDepartment(state);
  const hasAccessToCaseResolutionReview =
    getAccessToCaseResolutionReview(state);
  const hasAccessToSettings = getAccessToSettings(state);
  const hasAccessToRegulatoryReport = getAccessToRegulatoryReport(state);
  const hasAccessToOperationalReport = getAccessToOperationalReport(state);
  const hasAccessToWhiteList = getAccessToWhiteList(state);
  const canSeeFramlView = getCanSeeFramlView(state);

  return {
    hasAccessToDashboard,
    hasAccessToAlertList,
    hasAccessToAlertHistory,
    hasAccessToCasesByDepartment,
    hasAccessToCaseResolutionReview,
    hasAccessToSettings,
    hasAccessToRegulatoryReport,
    hasAccessToOperationalReport,
    hasAccessToWhiteList,
    canSeeFramlView
  };
};

export default connect(mapStateToProps)(SidebarItems);
